import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from '@components/framework/input/InputNumber';
import { MultiSelect } from 'primereact/multiselect';
import { Chips } from 'primereact/chips';
import { Calendar } from 'primereact/calendar';
import { EntityTypeEnum, FactMetadataOutDTO, FactTypeEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { amountOf, durationValue, isQuantityValue, isRelativeTimeValue, OperatorKey, pluralDurationUnit, relativeTime, RuleValue, unitOf, withAmount, withUnit } from '../../ruleAst';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency';
import { useEntityRefOptions } from './entityRefOptions';
import { enumValueOptions } from '../../i18nKeys';
import { useRuleMetadata } from '../../metadata/RuleMetadataProvider';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import RingilLabelForm from '@components/RingilComponents/LabelForm/RingilLabelForm';
import { descriptorForComparison, findCreatedOption, ValueDescriptor } from './valueDescriptor';

/**
 * Units of the same dimension, from `dsl/Literals.kt`. A value may be written in any unit of its
 * fact's dimension — the backend converts at comparison time — so the selector offers the whole
 * dimension rather than just the fact's own unit.
 */
const UNIT_DIMENSIONS: Record<string, string[]> = {
  kg: ['kg', 't'],
  t: ['kg', 't'],
  km: ['km'],
  '%': ['%'],
};

/**
 * `Keywords.DURATION_UNITS`. Singular and plural both parse; the plural reads better in a list.
 *
 * The value is the DSL keyword and never translated — it is written into the rule text — while the
 * label beside it is, since "weeks" is the one word in this control a reader has to understand.
 */
const DURATION_UNITS = ['minutes', 'hours', 'days', 'weeks'];

const durationUnitOptions = (tr: (key: string, defaultText?: string) => string) =>
  DURATION_UNITS.map(unit => ({ label: tr(`AiDispatcher.durationUnit.${unit}`, unit), value: unit }));

export type { ValueDescriptor } from './valueDescriptor';
export { descriptorOfFact } from './valueDescriptor';

interface ComponentProps {
  fact: FactMetadataOutDTO;
  operator: OperatorKey;
  value: RuleValue;
  onChange: (value: RuleValue) => void;
  companyId?: string;
  disabled?: boolean;
}

const asOptions = (values: string[]) => values.map(value => ({ label: value, value }));

/**
 * The value widget for one comparison, chosen entirely from the fact's `factType` and the
 * operator. Nothing here is keyed on a specific fact — a new fact from the backend renders
 * correctly without a frontend change. An operator that names its own value list (`in group`)
 * overrides the fact's — see descriptorForComparison.
 */
const ValueInput: React.FC<ComponentProps> = ({ fact, operator, value, onChange, companyId, disabled }) => {
  const { tr } = useTranslationLgs();
  const { operatorByKey } = useRuleMetadata();
  const descriptor = descriptorForComparison(fact, operatorByKey(operator));
  const entityRef = useEntityRefOptions(descriptor.entityRefType, companyId);

  const isList = operator === 'IN' || operator === 'NOT_IN';
  const isRange = operator === 'BETWEEN';

  if (isList) {
    const listValue = Array.isArray(value) ? value : [];
    // A list of entities is picked by name, never typed as ids.
    if (entityRef.supported) {
      return (
        <MultiSelect
          className="w-full"
          value={listValue as string[]}
          options={entityRef.options}
          onChange={event => onChange(event.value)}
          disabled={disabled || entityRef.loading}
          filter={entityRef.options.length > 10}
          display="chip"
          placeholder={entityRef.loading ? tr('AiDispatcher.loadingOptions', 'Loading…') : tr('AiDispatcher.selectValues', 'Select values')}
        />
      );
    }
    if (descriptor.valueType === String(FactTypeEnum.ENUM)) {
      return (
        <MultiSelect
          className="w-full"
          value={listValue as string[]}
          options={enumValueOptions(tr, descriptor.enumValues, descriptor.enumKeyPrefix)}
          onChange={event => onChange(event.value)}
          disabled={disabled}
          filter={(descriptor.enumValues ?? []).length > 10}
          placeholder={tr('AiDispatcher.selectValues', 'Select values')}
        />
      );
    }
    return (
      <Chips
        className="w-full"
        value={listValue.map(item => String(item))}
        onChange={event => onChange(event.value ?? [])}
        disabled={disabled}
        placeholder={tr('AiDispatcher.typeAndEnter', 'Type a value and press Enter')}
      />
    );
  }

  if (isRange) {
    const range = Array.isArray(value) ? value : [];
    const setBound = (index: number, bound: RuleValue) => {
      const next: RuleValue[] = [range[0] ?? null, range[1] ?? null];
      next[index] = bound;
      onChange(next);
    };
    // A date bound is a mode switch plus its inputs — two of those side by side outgrow the value
    // column and run under the row's buttons, so date ranges stack. Other ranges stay on one line.
    const stacked = descriptor.valueType === String(FactTypeEnum.DATETIME);
    return (
      <div className={stacked ? 'flex flex-col gap-2' : 'flex flex-wrap items-center gap-2'}>
        <ScalarInput descriptor={descriptor} value={range[0] ?? null} onChange={bound => setBound(0, bound)} companyId={companyId} disabled={disabled} />
        <span className="text-sm text-content-secondary">{tr('AiDispatcher.and', 'and')}</span>
        <ScalarInput descriptor={descriptor} value={range[1] ?? null} onChange={bound => setBound(1, bound)} companyId={companyId} disabled={disabled} />
      </div>
    );
  }

  return <ScalarInput descriptor={descriptor} value={value} onChange={onChange} companyId={companyId} disabled={disabled} />;
};

interface ScalarProps {
  descriptor: ValueDescriptor;
  value: RuleValue;
  onChange: (value: RuleValue) => void;
  companyId?: string;
  disabled?: boolean;
}

/** One scalar value, chosen from its declared type. Shared by conditions and action parameters. */
export const ScalarInput: React.FC<ScalarProps> = ({ descriptor, value, onChange, companyId, disabled }) => {
  const { tr } = useTranslationLgs();
  const entityRef = useEntityRefOptions(descriptor.entityRefType, companyId);
  const [creatingLabel, setCreatingLabel] = useState(false);

  // A list of entities — `add_label(labels: [...])` — is picked several at a time. A value saved as
  // a single string (the old single-select) reads as a one-item list; the backend accepts both.
  const listValue = Array.isArray(value) ? (value as string[]) : typeof value === 'string' && value ? [value] : [];

  // An action may take a label that does not exist yet: it is created through the standard label
  // endpoint, the list reloaded (in every picker on the page) and the new label selected. Only
  // own-company pickers get here — `supported` is false in the admin section, as is the endpoint.
  const canCreateLabel = descriptor.creatable && descriptor.entityRefType === 'COMPANY_LABEL' && entityRef.supported && !disabled;
  const labelCreation = canCreateLabel && (
    <>
      {creatingLabel ? (
        <div className="flex flex-col gap-3 rounded-md border border-solid border-base-tertiary p-3">
          <RingilLabelForm
            entityType={EntityTypeEnum.EXPEDITION}
            onCancel={() => setCreatingLabel(false)}
            onCreated={name => {
              const before = entityRef.options;
              entityRef.reload().then(after => {
                const created = findCreatedOption(before, after, name);
                if (created) onChange(descriptor.multiple ? [...listValue, created.value] : created.value);
                setCreatingLabel(false);
              });
            }}
          />
        </div>
      ) : (
        <div>
          <RingilButton mode="text-only" label={tr('AiDispatcher.createLabel', 'Create new label')} onClick={() => setCreatingLabel(true)} />
        </div>
      )}
    </>
  );
  const withLabelCreation = (picker: React.ReactNode) =>
    labelCreation ? (
      <div className="flex flex-col gap-2">
        {picker}
        {labelCreation}
      </div>
    ) : (
      picker
    );

  if (entityRef.supported && descriptor.multiple) {
    return withLabelCreation(
      <MultiSelect
        className="w-full"
        value={listValue}
        options={entityRef.options}
        onChange={event => onChange(event.value ?? [])}
        disabled={disabled || entityRef.loading}
        filter={entityRef.options.length > 10}
        display="chip"
        placeholder={entityRef.loading ? tr('AiDispatcher.loadingOptions', 'Loading…') : tr('AiDispatcher.selectValues', 'Select values')}
      />,
    );
  }

  // An entity reference is picked by name whatever its declared type: the code-based lookups are
  // STRING facts that carry an entityRefType, so this has to come before the type switch.
  if (entityRef.supported) {
    return withLabelCreation(
      <Dropdown
        className="w-full"
        value={typeof value === 'string' ? value : null}
        options={entityRef.options}
        onChange={event => onChange(event.value)}
        disabled={disabled || entityRef.loading}
        filter={entityRef.options.length > 10}
        showClear
        placeholder={entityRef.loading ? tr('AiDispatcher.loadingOptions', 'Loading…') : tr('AiDispatcher.selectValue', 'Select a value')}
      />,
    );
  }

  switch (descriptor.valueType as FactTypeEnum) {
    case FactTypeEnum.ENUM:
      return (
        <Dropdown
          className="w-full"
          value={typeof value === 'string' ? value : null}
          options={enumValueOptions(tr, descriptor.enumValues, descriptor.enumKeyPrefix)}
          onChange={event => onChange(event.value)}
          disabled={disabled}
          filter={(descriptor.enumValues ?? []).length > 10}
          placeholder={tr('AiDispatcher.selectValue', 'Select a value')}
        />
      );

    case FactTypeEnum.BOOLEAN:
      return (
        <Dropdown
          className="w-full"
          value={typeof value === 'boolean' ? value : null}
          options={[
            { label: tr('AiDispatcher.true', 'Yes'), value: true },
            { label: tr('AiDispatcher.false', 'No'), value: false },
          ]}
          onChange={event => onChange(event.value)}
          disabled={disabled}
        />
      );

    case FactTypeEnum.MONEY:
      // The same price-and-currency control the rest of the app uses; it resolves the currency
      // list for the right company on its own.
      return (
        <InputPriceWithCurrency
          value={amountOf(value) ?? null}
          currency={unitOf(value) ?? null}
          changePrice={amount => onChange({ value: amount ?? 0, currency: unitOf(value) ?? null })}
          changeCurrency={currency => onChange({ value: amountOf(value) ?? 0, currency })}
          companyId={companyId}
          disabled={disabled}
        />
      );

    case FactTypeEnum.NUMBER: {
      const units = UNIT_DIMENSIONS[descriptor.unit ?? ''] ?? [];
      return (
        <div className="flex items-center gap-2">
          <InputNumber
            className="w-32"
            value={amountOf(value) ?? null}
            onValueChange={event => onChange(withAmount(value, event.value ?? 0))}
            disabled={disabled}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={4}
          />
          {units.length > 1 ? (
            <Dropdown
              className="w-24"
              value={unitOf(value) ?? descriptor.unit}
              options={asOptions(units)}
              onChange={event => onChange(withUnit(value, event.value))}
              disabled={disabled}
            />
          ) : (
            descriptor.unit && <span className="text-content-secondary">{descriptor.unit}</span>
          )}
        </div>
      );
    }

    case FactTypeEnum.DURATION: {
      // The selector lists plural keywords, so a stored `1 day` is shown under `days`; the stored
      // spelling only changes when the author edits it. Negative amounts are legal (overlapping
      // windows), so the input has no minimum.
      const unit = pluralDurationUnit(isQuantityValue(value) ? value.unit : undefined) ?? 'minutes';
      return (
        <div className="flex items-center gap-2">
          <InputNumber
            className="w-32"
            value={amountOf(value) ?? null}
            onValueChange={event => onChange(durationValue(event.value, unit))}
            disabled={disabled}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={2}
          />
          <Dropdown
            className="w-32"
            value={unit}
            options={durationUnitOptions(tr)}
            onChange={event => onChange(durationValue(amountOf(value), event.value))}
            disabled={disabled}
          />
        </div>
      );
    }

    case FactTypeEnum.DATETIME:
      return <DateTimeInput value={value} onChange={onChange} disabled={disabled} />;

    // ENTITY_REF has no picker yet: `entityRefType` names a type whose picker we cannot map
    // generically, so the id is entered directly and validated by the backend. Real pickers get
    // added per `entityRefType` as they appear, without changing anything else here.
    case FactTypeEnum.ENTITY_REF:
    case FactTypeEnum.LIST:
    case FactTypeEnum.STRING:
    default:
      return (
        <div className="flex flex-col gap-1">
          <InputText
            className="w-full"
            value={value === null || value === undefined ? '' : String(value)}
            onChange={event => onChange(event.target.value)}
            disabled={disabled}
            placeholder={descriptor.entityRefType ?? undefined}
          />
          {/* Only reached for an entity we cannot list here — say so, rather than leaving an
              unexplained id box. */}
          {descriptor.entityRefType && !entityRef.supported && (
            <span className="text-xs text-content-tertiary">
              {tr('AiDispatcher.entityRefNotSelectable', 'This list cannot be shown for another company yet — paste the id.')}
            </span>
          )}
        </div>
      );
  }
};

type DateTimeAnchor = 'DATE' | 'PLUS' | 'MINUS';

/**
 * A fixed date, or a time relative to when the rule runs (`now + 2 hours`), on one line.
 *
 * The first dropdown is both the mode switch and the sign — *Fixed date*, `now +`, `now −` — so a
 * relative time is three compact controls instead of a switch row plus four inputs, and a stacked
 * `between` stays two lines. The mode is read from the value, so a rule written in text mode opens
 * in the right one; switching between a date and an offset drops the value, as they share nothing.
 *
 * Sign and unit are also kept in local state: an amount of 0 is stored as plain `now`, which
 * carries neither, and picking the unit before typing the number must not throw it away.
 */
const DateTimeInput: React.FC<{ value: RuleValue; onChange: (value: RuleValue) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
  const { tr } = useTranslationLgs();
  const relative = isRelativeTimeValue(value) ? value : undefined;
  const [sign, setSign] = useState<'+' | '-'>(relative?.value < 0 ? '-' : '+');
  const [unit, setUnit] = useState<string>(pluralDurationUnit(relative?.unit) ?? 'hours');
  const amount = relative?.value ? Math.abs(relative.value) : null;

  // A value replaced from outside (text mode, undo) brings its own sign and unit.
  useEffect(() => {
    if (relative?.value) setSign(relative.value < 0 ? '-' : '+');
    if (relative?.unit) setUnit(pluralDurationUnit(relative.unit));
  }, [relative?.value, relative?.unit]);

  const anchor: DateTimeAnchor = !relative ? 'DATE' : sign === '-' ? 'MINUS' : 'PLUS';

  const changeAnchor = (next: DateTimeAnchor) => {
    if (next === anchor) return;
    if (next === 'DATE') {
      onChange(null);
      return;
    }
    const nextSign = next === 'MINUS' ? '-' : '+';
    setSign(nextSign);
    // From a date there is no amount to keep; between `now +` and `now −` the amount stays.
    onChange(relativeTime(nextSign, relative ? amount : null, unit));
  };

  return (
    <div className="flex items-center gap-2">
      <Dropdown
        className="w-36 shrink-0"
        value={anchor}
        options={[
          { label: tr('AiDispatcher.dateModeFixed', 'Fixed date'), value: 'DATE' },
          { label: 'now +', value: 'PLUS' },
          { label: 'now −', value: 'MINUS' },
        ]}
        onChange={event => changeAnchor(event.value)}
        disabled={disabled}
      />
      {anchor === 'DATE' ? (
        <Calendar
          className="min-w-[11rem] flex-1"
          inputClassName="h-11 w-full"
          value={typeof value === 'string' && value ? new Date(value) : null}
          onChange={event => onChange(event.value ? (event.value as Date).toISOString() : null)}
          showTime
          showButtonBar
          disabled={disabled}
        />
      ) : (
        <>
          <InputNumber
            className="w-20 shrink-0"
            inputClassName="h-11 w-full"
            value={amount}
            onValueChange={event => onChange(relativeTime(sign, event.value ?? null, unit))}
            min={0}
            placeholder="0"
            disabled={disabled}
          />
          <Dropdown
            className="w-36 shrink-0"
            value={unit}
            options={durationUnitOptions(tr)}
            onChange={event => {
              setUnit(event.value);
              onChange(relativeTime(sign, amount, event.value));
            }}
            disabled={disabled}
          />
        </>
      )}
    </div>
  );
};

export default ValueInput;
